import React, { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { loginUser } from '../../api/user/auth';
import { dashboardsFor, LIST_PROPERTY_PATH } from '../../features/dashboards';
import { hotelPaths } from '../../features/hotel/paths';
import styles from '../register/propertySeeker/Auth.module.css';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async(e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await loginUser(formData.email, formData.password);
      console.log('Login Status', response);

      if(response.success){
        localStorage.setItem('user', JSON.stringify(response.user));

        const intent = location.state?.intent || localStorage.getItem('onboarding_intent');
        localStorage.removeItem('onboarding_intent');
        // Set by route guards (e.g. /property) that sent the user here to sign in
        const returnTo = location.state?.from;

        const queryParams = new URLSearchParams(location.search);
        const fromQuery = queryParams.get('from');
        const from = location.state?.from || fromQuery;

        if (from) {
          navigate(from, { replace: true });
        } else if (returnTo) {
          navigate(returnTo, { replace: true });
        } else if (intent === 'find_service') {
          navigate('/serviceProvider', { replace: true });
        } else if (intent === 'find_house') {
          navigate('/rent', { replace: true });
        } else if (intent === 'become_service_provider') {
          navigate('/update-account', { replace: true, state: { openServiceModal: true } });
        } else if (intent === 'become_property_owner') {
          navigate(LIST_PROPERTY_PATH, { replace: true });
        } else {
          // A property owner or provider goes straight to their dashboard; users with
          // both roles (or none) land on the home page and use the header shortcuts
          const dashboards = dashboardsFor(response.user);
          navigate(dashboards.length === 1 ? dashboards[0].to : '/', { replace: true });
        }
      } else if (response.requiresVerification) {
        // Redirect to verification page with user ID
        navigate(`/verify-email/${response.userId}`, { 
          state: { 
            email: response.email,
            message: response.message
          }
        });
      } else {
        setError(response.message)
      }
    } catch (error) {
      setError(error.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className={styles.authContainer}>
      <div className={styles.backgroundAnimation}></div>
      
      <div className={`${styles.authCard} ${styles.userCard}`}>
        <div className={styles.authHeader}>
          <div className={styles.authLogo}>SPOTFLI</div>
          <h2 className={styles.authTitle}>Welcome Back</h2>
          <p className={styles.authSubtitle}>Sign in to your account to continue</p>
        </div>

        {error && (
          <div className={styles.errorMessage}>
            <i className={`fa fa-exclamation-circle ${styles.errorIcon}`}></i>
            {error}
          </div>
        )}

        <form className={styles.authForm} onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel} htmlFor="email">Email Address</label>
            <input 
              className={styles.formInput}
              type="email" 
              placeholder='your.email@example.com' 
              name='email' 
              value={formData.email} 
              onChange={handleChange} 
              required
            />
          </div>

          <div className={styles.formGroup}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label className={styles.formLabel} htmlFor="password">Password</label>
              <Link to="/forgot-password" className={styles.authLink} style={{ fontSize: '0.85rem' }}>Forgot Password?</Link>
            </div>
            <div className={styles.passwordInputContainer}>
              <input 
                className={styles.formInput}
                type={showPassword ? "text" : "password"}
                name='password' 
                value={formData.password} 
                onChange={handleChange}
                placeholder='Enter your password'
                required
              />
              <button 
                type="button" 
                className={styles.passwordToggle}
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>
          </div>

          <button 
            className={styles.authButton} 
            disabled={isLoading} 
            type="submit"
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </button>

          <div className={styles.authLinks}>
            <span>Don't have an account? </span>
            <Link to="/register" className={styles.authLink}>Create Account</Link>
          </div>

          {/* Hotels have their own account type and sign in on the hotel portal */}
          <div className={styles.authLinks}>
            <span>Managing a hotel? </span>
            <Link to={hotelPaths.login} className={styles.authLink}>Hotel portal login</Link>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Login;